import { AInput } from "@/admin/form/Fields.tsx";
import { adminUserCreateMutation, elevatedUserRoles } from "@/admin/users/adminUsersQuery.ts";
import LR from "@/components/LeftRight.module.css";
import { WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { ElevatedUserRole, ElevatedUserUpsertBody } from "@warrenio/api-spec/spec.oats.gen";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

export function AddAdminUserButton({ button }: { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddAdminUserModalContent />
        </WModal>
    );
}

export function EditAdminUserButton({ item }: { item: ElevatedUserUpsertBody }) {
    return (
        <WModal
            button={
                <WTooltip text="Edit">
                    <WButton color="primary" size="xs" variant="ghost" icon="jp-icon-edit" action={undefined} />
                </WTooltip>
            }
        >
            <AddAdminUserModalContent item={item} />
        </WModal>
    );
}

interface AddAdminUserInputs {
    username: string;
    role: ElevatedUserRole;
}

function AddAdminUserModalContent({ item }: { item?: ElevatedUserUpsertBody }) {
    const createMutation = useStandardMutation(adminUserCreateMutation);

    const onSubmit: SubmitHandler<AddAdminUserInputs> = async (inputs) => {
        const { username, role } = inputs;

        await createMutation.mutateAsync({
            body: {
                username,
                role,
            },
        });
    };

    const form = useForm<AddAdminUserInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            username: item?.username || "",
            role: item?.role || "admin",
        },
    });

    const { control } = form;

    return (
        <WModalForm title="Add New Admin User" label={item ? "Edit" : "Add"} form={form} onSubmit={onSubmit}>
            <Controller
                control={control}
                name="username"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField block headless autoFocus label="Username" {...formPropsToAriaProps(props)}>
                        <AInput />
                    </WTextField>
                )}
            />
            <Controller
                control={control}
                name="role"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <FormField headless wide label="Role">
                        <WSelect
                            isRequired
                            getKey={(item) => item.id}
                            items={elevatedUserRoles.map((role) => ({
                                id: role,
                                title: role,
                            }))}
                            itemClassName={LR.item}
                            valueClassName={LR.value}
                            aria-label="Select role"
                            getTextValue={(item) => item.title}
                            valueKey={props.field.value}
                            onChange={(item) => props.field.onChange(item.id)}
                        >
                            {({ title }) => title}
                        </WSelect>
                    </FormField>
                )}
            />
        </WModalForm>
    );
}
